import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  isRepertoireEntryFormDirty,
  type RepertoireEntryFormDirtyState,
} from '@/lib/repertoire-entry-form-dirty';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Micro } from '@/components/atoms/micro';
import { MasteryOptionCard } from '@/components/molecules/mastery-option-card';
import { SettingPickerCard } from '@/components/molecules/setting-picker-card';
import { PriorityOptionRow } from '@/components/molecules/priority-option-row';
import { FirstPracticeChoiceCard } from '@/components/molecules/first-practice-choice-card';
import { FormSummaryBand } from '@/components/molecules/form-summary-band';
import {
  masteryVisualLevel,
  visualLevelToSubmitSortOrder,
  VISUAL_LEVEL_DESCRIPTORS,
  type MasteryVisualLevel,
} from '@/lib/mastery';
import { QUEUE_STATUS_PRESENTATION } from '@/lib/queue-status';
import {
  resolveFirstPractice,
  prefillFirstPractice,
  type FirstPracticeChoice,
} from '@/lib/first-practice';
import { buildAddRepertoireSummary } from '@/lib/repertoire-entry-summary';
import type { Arrangement, Form, Key, MasteryLevel, QueueStatus, RepertoireEntry, TimeSignature } from '@/lib/types';
import { useTonicAndMode } from '@/lib/hooks/use-tonic-and-mode';

export type RepertoireEntryFormData = {
  masteryLevelId: string | null;
  queueStatusId: string;
  keyId: string | null;
  // Arrangement-shared fields — only present in edit mode (the create endpoint
  // doesn't accept them).
  timeSignatureId?: string | null;
  formId?: string | null;
  prettyName?: string;
  practiceTempoBeats: number | null;
  performanceTempoBeats: number | null;
  notes: string | null;
  firstPracticeOn?: string;
};

type SharedProps = {
  masteryLevels: MasteryLevel[];
  queueStatuses: QueueStatus[];
  keys: Key[];
  timeSignatures: TimeSignature[];
  forms?: Form[];
  onSave: (data: RepertoireEntryFormData) => void;
  onCancel: () => void;
  onRemove?: () => Promise<void>;
  isSaving: boolean;
  submitLabel?: string;
  showDangerZone?: boolean;
  showSharedMetadata?: boolean;
  today?: Date;
  variant?: 'page' | 'sheet' | 'embedded';
  /**
   * Optional `<form id>` used by the embedded variant so an external footer
   * (e.g. the F4 right-pane summary band) can submit via `<button form={id}>`.
   */
  htmlFormId?: string;
  /**
   * Fires whenever the form transitions between clean and dirty. The wrapping
   * surface (e.g. the F4 right-pane organism) uses this to gate cancel /
   * piece-switch behind a discard-changes dialog.
   */
  onDirtyChange?: (isDirty: boolean) => void;
};

export type RepertoireEntryFormProps =
  | (SharedProps & { mode: 'edit'; entry: RepertoireEntry })
  | (SharedProps & {
      mode: 'create';
      arrangements: Arrangement[];
      selectedArrangementId: string;
      onSelectArrangement?: (id: string) => void;
    });

type Initial = {
  visualMastery: MasteryVisualLevel;
  queueStatusId: string;
  keyId: string | null;
  timeSignatureId: string | null;
  formId: string | null;
  practiceTempoRaw: string;
  performanceTempoRaw: string;
  notesRaw: string;
  pieceName: string;
  arrangementName: string;
  formName: string | null;
  firstPracticeChoice: FirstPracticeChoice | null;
  firstPracticeCustomDate: string | null;
  resetKey: string;
};

function parseTempo(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function deriveVisualMastery(entry: RepertoireEntry, masteryLevels: MasteryLevel[]): MasteryVisualLevel {
  const level = masteryLevels.find((m) => m.masteryLevelId === entry.masteryLevelId);
  return masteryVisualLevel(level?.sortOrder);
}

function deriveFirstPracticeInitial(
  firstPracticeOn: string | null,
  today: Date,
): { choice: FirstPracticeChoice | null; customDate: string | null } {
  if (firstPracticeOn === null) return { choice: null, customDate: null };
  const choice = prefillFirstPractice(firstPracticeOn, today);
  const customDate = choice === 'custom' ? firstPracticeOn : null;
  return { choice, customDate };
}

function deriveInitial(props: RepertoireEntryFormProps, today: Date): Initial {
  if (props.mode === 'edit') {
    const { entry, masteryLevels } = props;
    const { choice, customDate } = deriveFirstPracticeInitial(entry.firstPracticeOn, today);
    return {
      visualMastery: deriveVisualMastery(entry, masteryLevels),
      queueStatusId: entry.queueStatusId,
      keyId: entry.keyId,
      timeSignatureId: entry.timeSignatureId,
      formId: entry.formId,
      practiceTempoRaw: entry.practiceTempoBeats?.toString() ?? '',
      performanceTempoRaw: entry.performanceTempoBeats?.toString() ?? '',
      notesRaw: entry.notes ?? '',
      pieceName: entry.pieceName,
      arrangementName: entry.arrangementName,
      formName: entry.formName,
      firstPracticeChoice: choice,
      firstPracticeCustomDate: customDate,
      resetKey: `edit:${entry.userArrangementId}`,
    };
  }
  const { arrangements, selectedArrangementId, queueStatuses } = props;
  const currentArrangement =
    arrangements.find((a) => a.arrangementId === selectedArrangementId) ?? arrangements[0];
  const defaultQueueStatusId = queueStatuses[0]?.queueStatusId ?? '';
  return {
    visualMastery: 'new',
    queueStatusId: defaultQueueStatusId,
    keyId: currentArrangement?.keyId ?? null,
    timeSignatureId: currentArrangement?.timeSignatureId ?? null,
    formId: currentArrangement?.formId ?? null,
    practiceTempoRaw: '',
    performanceTempoRaw: '',
    notesRaw: '',
    pieceName: currentArrangement?.pieceName ?? '',
    arrangementName: currentArrangement?.prettyName ?? '',
    formName: currentArrangement?.formName ?? null,
    firstPracticeChoice: null,
    firstPracticeCustomDate: null,
    resetKey: `create:${selectedArrangementId}:${defaultQueueStatusId}`,
  };
}

const FALLBACK_ICON = 'pause' as const;
const NOTES_MAX_LENGTH = 2000;

export function RepertoireEntryForm(props: RepertoireEntryFormProps): ReactElement {
  const {
    masteryLevels,
    queueStatuses,
    keys,
    timeSignatures,
    forms = [],
    onSave,
    onCancel,
    onRemove,
    isSaving,
    submitLabel,
    showDangerZone = true,
    showSharedMetadata = true,
    today = new Date(),
    variant = 'page',
    htmlFormId,
    onDirtyChange,
  } = props;
  const initial = deriveInitial(props, today);

  const [visualMastery, setVisualMastery] = useState<MasteryVisualLevel>(initial.visualMastery);
  const [queueStatusId, setQueueStatusId] = useState<string>(initial.queueStatusId);
  const [timeSignatureId, setTimeSignatureId] = useState<string | null>(initial.timeSignatureId);
  const [formId, setFormId] = useState<string | null>(initial.formId);
  const [arrangementName, setArrangementName] = useState<string>(initial.arrangementName);
  const [practiceTempoRaw, setPracticeTempoRaw] = useState<string>(initial.practiceTempoRaw);
  const [performanceTempoRaw, setPerformanceTempoRaw] = useState<string>(initial.performanceTempoRaw);
  const [notesRaw, setNotesRaw] = useState<string>(initial.notesRaw);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [firstPracticeChoice, setFirstPracticeChoice] = useState<FirstPracticeChoice | null>(
    initial.firstPracticeChoice,
  );
  const [firstPracticeCustomDate, setFirstPracticeCustomDate] = useState<string | null>(
    initial.firstPracticeCustomDate,
  );

  const { tonic, mode, setTonic, setMode, resetToKey, resolvedKeyId, availableTonics, availableModes, isPairAvailable } =
    useTonicAndMode(initial.keyId, keys);

  // In create mode, switching the selected arrangement should re-seed only
  // the arrangement-derived fields (key, time signature, arrangement name,
  // piece/form display) — user-entered personal fields (mastery, priority,
  // tempos, notes, first practice) must persist across the switch.
  useEffect(() => {
    resetToKey(initial.keyId);
    setTimeSignatureId(initial.timeSignatureId);
    setFormId(initial.formId);
    setArrangementName(initial.arrangementName);
    // Edit mode also re-seeds the personal fields because the target entry is
    // changing, not just the arrangement.
    if (props.mode === 'edit') {
      setVisualMastery(initial.visualMastery);
      setQueueStatusId(initial.queueStatusId);
      setPracticeTempoRaw(initial.practiceTempoRaw);
      setPerformanceTempoRaw(initial.performanceTempoRaw);
      setNotesRaw(initial.notesRaw);
      setFirstPracticeChoice(initial.firstPracticeChoice);
      setFirstPracticeCustomDate(initial.firstPracticeCustomDate);
    }
    // resetKey uniquely identifies the entry (edit) or arrangement (create)
    // being targeted, so this effect runs when the caller changes target.
    // keys.length is also a dependency so an arriving keys list (cold-cache
    // race) re-seeds tonic+mode via resetToKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.resetKey, keys.length]);

  // Cold-cache race: in create mode the queue-status default depends on the
  // reference-data query, which may arrive after the first paint. The
  // resetKey effect above doesn't re-seed personal fields in create mode (by
  // design — switching arrangement should NOT reset what the user typed). So
  // we seed the queue status separately, only when it's still empty (i.e.
  // the user hasn't picked one yet).
  useEffect(() => {
    if (queueStatusId === '' && initial.queueStatusId !== '') {
      setQueueStatusId(initial.queueStatusId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.queueStatusId]);

  const dirtyInitial: RepertoireEntryFormDirtyState = {
    visualMastery: initial.visualMastery,
    queueStatusId: initial.queueStatusId,
    keyId: initial.keyId,
    practiceTempoRaw: initial.practiceTempoRaw,
    performanceTempoRaw: initial.performanceTempoRaw,
    notesRaw: initial.notesRaw,
    firstPracticeChoice: initial.firstPracticeChoice,
    firstPracticeCustomDate: initial.firstPracticeCustomDate,
  };
  const dirtyCurrent: RepertoireEntryFormDirtyState = {
    visualMastery,
    queueStatusId,
    keyId: resolvedKeyId,
    practiceTempoRaw,
    performanceTempoRaw,
    notesRaw,
    firstPracticeChoice,
    firstPracticeCustomDate,
  };
  const isDirty = isRepertoireEntryFormDirty({ current: dirtyCurrent, initial: dirtyInitial });
  const lastDirtyRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (onDirtyChange === undefined) return;
    if (lastDirtyRef.current === isDirty) return;
    lastDirtyRef.current = isDirty;
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const isCustomDateMissing = firstPracticeChoice === 'custom' && !firstPracticeCustomDate;

  const handleFirstPracticeChange = (choice: FirstPracticeChoice, customDate?: string): void => {
    setFirstPracticeChoice(choice);
    setFirstPracticeCustomDate(customDate ?? null);
  };

  const buildPayload = (): RepertoireEntryFormData => {
    const targetSortOrder = visualLevelToSubmitSortOrder(visualMastery);
    const masteryLevelId = masteryLevels.find((m) => m.sortOrder === targetSortOrder)?.masteryLevelId ?? null;

    const firstPracticeOn =
      firstPracticeChoice !== null
        ? resolveFirstPractice(firstPracticeChoice, today, firstPracticeCustomDate ?? undefined)
        : undefined;

    // prettyName + timeSignatureId are arrangement-shared edit-only fields —
    // AddToRepertoireRequest does not accept them, so omit in create mode.
    const sharedArrangementFields =
      props.mode === 'edit'
        ? { timeSignatureId, formId, prettyName: arrangementName }
        : {};

    return {
      masteryLevelId,
      queueStatusId,
      keyId: resolvedKeyId,
      practiceTempoBeats: parseTempo(practiceTempoRaw),
      performanceTempoBeats: parseTempo(performanceTempoRaw),
      notes: notesRaw.trim() === '' ? null : notesRaw,
      ...sharedArrangementFields,
      ...(firstPracticeOn !== undefined ? { firstPracticeOn } : {}),
    };
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSave(buildPayload());
  };

  const handleBandSubmit = (): void => {
    onSave(buildPayload());
  };

  const subtitle = [initial.formName].filter(Boolean).join(' • ');
  const resolvedSubmitLabel = submitLabel ?? (props.mode === 'create' ? 'Add to repertoire' : 'Save changes');

  const masteryShortLabel = VISUAL_LEVEL_DESCRIPTORS.find((d) => d.level === visualMastery)?.level ?? null;
  const queueStatusName = queueStatuses.find((q) => q.queueStatusId === queueStatusId)?.prettyName ?? null;
  const canSubmit = !isSaving && !!queueStatusId && !isCustomDateMissing;

  const summaryText = buildAddRepertoireSummary({
    mode: props.mode === 'edit' ? 'edit' : 'create',
    arrangementName: initial.arrangementName,
    pieceName: initial.pieceName || null,
    masteryShortLabel,
    queueStatusName,
    firstPracticeChoice,
    tunebookCount: 0,
  });

  return (
    <form id={htmlFormId} onSubmit={handleSubmit} className="flex flex-col gap-8">
      {variant !== 'embedded' && (
        <header>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[var(--ink)] md:text-4xl">
            {initial.pieceName}
          </h2>
          {subtitle && <p className="mt-1 text-sm text-[var(--ink-3)]">{subtitle}</p>}
        </header>
      )}

      {variant === 'page' && props.mode === 'create' && props.arrangements.length > 1 && (
        <fieldset role="radiogroup" aria-label="Setting" className="flex flex-col gap-3">
          <legend className="sr-only">Setting</legend>
          <Micro>Setting</Micro>
          <div className="flex flex-col gap-2">
            {props.arrangements.map((arr) => (
              <SettingPickerCard
                key={arr.arrangementId}
                arrangement={arr}
                selected={arr.arrangementId === props.selectedArrangementId}
                onSelect={() => props.onSelectArrangement?.(arr.arrangementId)}
              />
            ))}
          </div>
        </fieldset>
      )}

      {variant === 'page' && props.mode === 'create' && props.arrangements.length === 1 && (
        <section aria-label="Setting" className="flex flex-col gap-1 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] p-4">
          <Micro>Setting</Micro>
          <p className="text-sm font-medium text-[var(--ink)]">
            {props.arrangements[0].prettyName}
            {props.arrangements[0].keyName != null && ` · ${props.arrangements[0].keyName}`}
          </p>
        </section>
      )}

      {showSharedMetadata && (
        <section aria-labelledby="region-arrangement-metadata" className="flex flex-col gap-8">
          <h2 id="region-arrangement-metadata" className="sr-only">Arrangement metadata</h2>

          <p className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--warm-wash)] p-3 text-sm text-[var(--warm)]">
            Changes to arrangement metadata are visible to every user who has this arrangement.
          </p>

          <div className="flex flex-col gap-2">
            <Micro as="label" htmlFor="repertoire-entry-arrangement-name">Arrangement name</Micro>
            <input
              id="repertoire-entry-arrangement-name"
              type="text"
              value={arrangementName}
              onChange={(event) => setArrangementName(event.target.value)}
              disabled={isSaving}
              className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Micro as="label" htmlFor="repertoire-entry-tonic">Tonic</Micro>
              <select
                id="repertoire-entry-tonic"
                value={tonic ?? ''}
                onChange={(event) => setTonic(event.target.value)}
                disabled={isSaving}
                className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
              >
                <option value="">—</option>
                {availableTonics.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <Micro as="label" htmlFor="repertoire-entry-mode">Mode</Micro>
              <select
                id="repertoire-entry-mode"
                value={mode ?? ''}
                onChange={(event) => setMode(event.target.value)}
                disabled={isSaving}
                className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
              >
                <option value="">—</option>
                {availableModes.map((m) => (
                  <option key={m} value={m} disabled={tonic !== null && !isPairAvailable(tonic, m)}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Micro as="label" htmlFor="repertoire-entry-time-signature">Time signature</Micro>
            <select
              id="repertoire-entry-time-signature"
              value={timeSignatureId ?? ''}
              onChange={(event) => setTimeSignatureId(event.target.value || null)}
              disabled={isSaving}
              className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
            >
              <option value="">—</option>
              {timeSignatures.map((ts) => (
                <option key={ts.timeSignatureId} value={ts.timeSignatureId}>
                  {ts.prettyName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <Micro as="label" htmlFor="repertoire-entry-form">Form</Micro>
            <select
              id="repertoire-entry-form"
              value={formId ?? ''}
              onChange={(event) => setFormId(event.target.value || null)}
              disabled={isSaving}
              className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
            >
              <option value="">—</option>
              {forms.map((f) => (
                <option key={f.formId} value={f.formId}>
                  {f.prettyName}
                </option>
              ))}
            </select>
          </div>
        </section>
      )}

      <fieldset role="radiogroup" aria-label="Mastery" className="flex flex-col gap-3" disabled={isSaving}>
        <legend className="sr-only">Mastery</legend>
        <Micro>Mastery</Micro>
        <div className="flex flex-col gap-2">
          {VISUAL_LEVEL_DESCRIPTORS.map((descriptor) => (
            <MasteryOptionCard
              key={descriptor.level}
              descriptor={descriptor}
              selected={visualMastery === descriptor.level}
              onSelect={() => setVisualMastery(descriptor.level)}
            />
          ))}
        </div>
      </fieldset>

      <section aria-labelledby="region-personal-practice" className="flex flex-col gap-8">
        <h2 id="region-personal-practice" className="sr-only">Personal practice</h2>

        <fieldset role="radiogroup" aria-label="Priority" className="flex flex-col gap-3" disabled={isSaving}>
          <legend className="sr-only">Priority</legend>
          <Micro>Priority</Micro>
          <div className="flex flex-col gap-2">
            {queueStatuses.map((status) => {
              const presentation = QUEUE_STATUS_PRESENTATION[status.canonicalName] ?? {
                icon: FALLBACK_ICON,
                blurb: status.prettyName,
              };
              return (
                <PriorityOptionRow
                  key={status.queueStatusId}
                  icon={presentation.icon}
                  label={status.prettyName}
                  description={presentation.blurb}
                  selected={status.queueStatusId === queueStatusId}
                  onSelect={() => setQueueStatusId(status.queueStatusId)}
                />
              );
            })}
          </div>
        </fieldset>

        <fieldset role="radiogroup" aria-label="First practice" className="flex flex-col gap-3">
          <legend className="sr-only">First practice</legend>
          <Micro>First practice</Micro>
          <FirstPracticeChoiceCard
            choice={firstPracticeChoice}
            onChange={handleFirstPracticeChange}
            customDate={firstPracticeCustomDate ?? undefined}
          />
        </fieldset>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Micro as="label" htmlFor="repertoire-entry-practice-tempo">Practice tempo (bpm)</Micro>
            <input
              id="repertoire-entry-practice-tempo"
              type="number"
              min={20}
              max={300}
              value={practiceTempoRaw}
              onChange={(event) => setPracticeTempoRaw(event.target.value)}
              disabled={isSaving}
              placeholder="e.g. 90"
              className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Micro as="label" htmlFor="repertoire-entry-performance-tempo">Performance tempo (bpm)</Micro>
            <input
              id="repertoire-entry-performance-tempo"
              type="number"
              min={20}
              max={300}
              value={performanceTempoRaw}
              onChange={(event) => setPerformanceTempoRaw(event.target.value)}
              disabled={isSaving}
              placeholder="e.g. 120"
              className="h-12 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
            />
          </div>
        </div>

        {variant !== 'sheet' && (
          <div className="flex flex-col gap-2">
            <Micro as="label" htmlFor="repertoire-entry-notes">Notes for myself</Micro>
            <textarea
              id="repertoire-entry-notes"
              rows={4}
              value={notesRaw}
              onChange={(event) => {
                const capped = event.target.value.slice(0, NOTES_MAX_LENGTH);
                setNotesRaw(capped);
              }}
              disabled={isSaving}
              placeholder="Personal practice notes…"
              className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 py-3 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20 disabled:opacity-50"
            />
            {/* TODO: markdown rendering on notes — see ROADMAP follow-up "Markdown rendering for personal notes" */}
            <span className="text-right text-xs text-[var(--ink-3)]">
              {notesRaw.length}/{NOTES_MAX_LENGTH}
            </span>
          </div>
        )}
      </section>

      <section aria-label="Tags" aria-disabled="true" className="flex flex-col gap-3 rounded-[var(--r-sm)] border border-dashed border-[var(--rule)] bg-[var(--bg-soft)] p-4 opacity-50 cursor-not-allowed">
        <Micro>Tags</Micro>
        <p className="text-sm text-[var(--ink-3)]">Tags coming soon — see ROADMAP</p>
      </section>

      {variant === 'page' && (
        <section aria-label="Tunebooks" aria-disabled="true" className="flex flex-col gap-3 rounded-[var(--r-sm)] border border-dashed border-[var(--rule)] bg-[var(--bg-soft)] p-4 opacity-50 cursor-not-allowed">
          <Micro>Tunebooks</Micro>
          <p className="text-sm text-[var(--ink-3)]">Tunebooks coming soon — see ROADMAP</p>
        </section>
      )}

      {showDangerZone && (
        <section aria-labelledby="region-danger-zone" className="flex flex-col gap-4">
          <h2 id="region-danger-zone" className="sr-only">Danger zone</h2>
          <button
            type="button"
            onClick={() => setIsConfirmOpen(true)}
            className="inline-flex items-center gap-2 self-start rounded-[var(--r-sm)] border border-[var(--error,red)] px-4 h-10 text-sm font-semibold text-[var(--error,red)] hover:bg-[var(--error,red)]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--error,red)]"
          >
            <Trash2 className="h-4 w-4" />
            Remove from repertoire
          </button>

          {isConfirmOpen && (
            <dialog
              open
              aria-labelledby="confirm-remove-title"
              className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-6 shadow-xl backdrop:bg-black/40 w-full max-w-md"
            >
              <h3 id="confirm-remove-title" className="font-display text-lg font-semibold text-[var(--ink)]">
                Remove from repertoire
              </h3>
              <p className="mt-2 text-sm text-[var(--ink-3)]">
                Remove {initial.pieceName} from your repertoire? Practice history is kept, but the entry will disappear from your queue.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setRemoveError(null);
                    const result = onRemove?.() ?? Promise.resolve();
                    void result.then(() => {
                      setIsConfirmOpen(false);
                    }).catch((err: unknown) => {
                      const message = err instanceof Error ? err.message : 'Failed to remove from repertoire';
                      setRemoveError(message);
                    });
                  }}
                  className="inline-flex items-center gap-2 rounded-[var(--r-sm)] bg-[var(--error,red)] px-4 h-10 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--error,red)]"
                >
                  Remove permanently
                </button>
              </div>
              {removeError !== null && (
                <p role="alert" className="mt-3 text-sm text-[var(--error,red)]">
                  {removeError}
                </p>
              )}
            </dialog>
          )}
        </section>
      )}

      {variant === 'page' && (
        <FormSummaryBand
          summary={summaryText}
          submitLabel={resolvedSubmitLabel}
          onCancel={onCancel}
          onSubmit={handleBandSubmit}
          canSubmit={canSubmit}
          isSubmitting={isSaving}
        />
      )}
      {variant === 'sheet' && (
        <Button
          type="submit"
          variant="primary"
          disabled={!canSubmit}
        >
          {resolvedSubmitLabel}
        </Button>
      )}
    </form>
  );
}
